"use client";

import Link from "next/link";
import Script from "next/script";
import React, { useEffect, useState } from "react";
import { FaChevronDown, FaPhone, FaTag } from "react-icons/fa";

interface Grecaptcha {
  ready: (callback: () => void) => void;
  execute: (siteKey: string, options: { action: string }) => Promise<string>;
}

declare let grecaptcha: Grecaptcha;

interface BusinessHours {
  startTime: string; // Format: "HH:MM" (24-hour)
  endTime: string; // Format: "HH:MM" (24-hour)
}

interface BookAppointmentFormProps {
  overrideDisplay?: {
    enabled?: boolean;
    afterHoursMessage?: string;
  };
  weekdaySchedule?: {
    startTime?: string;
    endTime?: string;
  };
  sundaySchedule?: "off" | "custom";
  sundayTimeRange?: {
    startTime?: string;
    endTime?: string;
  };
}

export default function BookAppointmentForm(
  props: BookAppointmentFormProps = {}
) {
  const { overrideDisplay, weekdaySchedule, sundaySchedule, sundayTimeRange } =
    props;
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showTerms, setShowTerms] = useState(false);
  const [isBusinessHours, setIsBusinessHours] = useState(true);
  const [nextBusinessHour, setNextBusinessHour] = useState("");

  // Business hours configuration - use schedule if override is enabled, otherwise default (9 AM to 6 PM)
  const getBusinessHours = (): BusinessHours | null => {
    if (
      overrideDisplay?.enabled &&
      weekdaySchedule?.startTime &&
      weekdaySchedule?.endTime
    ) {
      return {
        startTime: weekdaySchedule.startTime,
        endTime: weekdaySchedule.endTime,
      };
    }
    return null;
  };

  const defaultBusinessHours: BusinessHours = {
    startTime: "09:00",
    endTime: "18:00",
  };

  const businessHours = getBusinessHours() || defaultBusinessHours;
  const afterHoursMessage =
    overrideDisplay?.afterHoursMessage ||
    "We'll call you back on the next business hour";

  const SITE_KEY = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY || "";

  // Check if current time is within business hours
  const checkBusinessHours = () => {
    const now = new Date();
    const currentDay = now.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
    const currentHours = now.getHours();
    const currentMinutes = now.getMinutes();
    const currentTimeInMinutes = currentHours * 60 + currentMinutes;

    // Handle Sunday
    if (currentDay === 0) {
      if (overrideDisplay?.enabled) {
        if (sundaySchedule === "off") {
          // Sunday is off, next is Monday
          const [startHours, startMins] = businessHours.startTime
            .split(":")
            .map(Number);
          const nextBusinessDate = new Date(now);
          nextBusinessDate.setDate(now.getDate() + 1);
          nextBusinessDate.setHours(startHours, startMins, 0, 0);
          setNextBusinessHour(formatNextBusinessHour(nextBusinessDate));
          setIsBusinessHours(false);
          return;
        } else if (
          sundaySchedule === "custom" &&
          sundayTimeRange?.startTime &&
          sundayTimeRange?.endTime
        ) {
          // Check if within Sunday custom hours
          const [startHours, startMins] = sundayTimeRange.startTime
            .split(":")
            .map(Number);
          const [endHours, endMins] = sundayTimeRange.endTime
            .split(":")
            .map(Number);
          const startTimeInMinutes = startHours * 60 + startMins;
          const endTimeInMinutes = endHours * 60 + endMins;

          const withinHours =
            currentTimeInMinutes >= startTimeInMinutes &&
            currentTimeInMinutes < endTimeInMinutes;

          if (withinHours) {
            setIsBusinessHours(true);
            setNextBusinessHour("");
            return;
          } else {
            // After Sunday hours, next is Monday
            const [weekdayStartHours, weekdayStartMins] =
              businessHours.startTime.split(":").map(Number);
            const nextBusinessDate = new Date(now);
            nextBusinessDate.setDate(now.getDate() + 1);
            nextBusinessDate.setHours(
              weekdayStartHours,
              weekdayStartMins,
              0,
              0
            );
            setNextBusinessHour(formatNextBusinessHour(nextBusinessDate));
            setIsBusinessHours(false);
            return;
          }
        }
      }
      // Default: Sunday is off
      const [startHours, startMins] = businessHours.startTime
        .split(":")
        .map(Number);
      const nextBusinessDate = new Date(now);
      nextBusinessDate.setDate(now.getDate() + 1);
      nextBusinessDate.setHours(startHours, startMins, 0, 0);
      setNextBusinessHour(formatNextBusinessHour(nextBusinessDate));
      setIsBusinessHours(false);
      return;
    }

    // Monday to Saturday
    const [startHours, startMins] = businessHours.startTime
      .split(":")
      .map(Number);
    const [endHours, endMins] = businessHours.endTime.split(":").map(Number);
    const startTimeInMinutes = startHours * 60 + startMins;
    const endTimeInMinutes = endHours * 60 + endMins;

    // Check if current time is within business hours
    const withinHours =
      currentTimeInMinutes >= startTimeInMinutes &&
      currentTimeInMinutes < endTimeInMinutes;

    if (withinHours) {
      setIsBusinessHours(true);
      setNextBusinessHour("");
    } else {
      setIsBusinessHours(false);
      // Calculate next business hour
      const nextBusinessDate = new Date(now);

      if (currentTimeInMinutes >= endTimeInMinutes) {
        // After business hours today, next is tomorrow at start time
        nextBusinessDate.setDate(now.getDate() + 1);
        nextBusinessDate.setHours(startHours, startMins, 0, 0);
      } else {
        // Before business hours today, next is today at start time
        nextBusinessDate.setHours(startHours, startMins, 0, 0);
      }

      // If next business hour is Sunday, move to Monday
      if (nextBusinessDate.getDay() === 0) {
        nextBusinessDate.setDate(nextBusinessDate.getDate() + 1);
        nextBusinessDate.setHours(startHours, startMins, 0, 0);
      }

      setNextBusinessHour(formatNextBusinessHour(nextBusinessDate));
    }
  };

  const formatNextBusinessHour = (date: Date): string => {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    const isToday = date.toDateString() === today.toDateString();
    const isTomorrow = date.toDateString() === tomorrow.toDateString();

    const hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? "PM" : "AM";
    const displayHours = hours % 12 || 12;
    const displayMinutes = minutes.toString().padStart(2, "0");
    const timeString = `${displayHours}:${displayMinutes} ${ampm}`;

    if (isToday) {
      return `today at ${timeString}`;
    } else if (isTomorrow) {
      return `tomorrow at ${timeString}`;
    } else {
      const dayNames = [
        "Sunday",
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday",
      ];
      const dayName = dayNames[date.getDay()];
      return `${dayName} at ${timeString}`;
    }
  };

  useEffect(() => {
    checkBusinessHours();
    // Update every minute to check if business hours have changed
    const interval = setInterval(checkBusinessHours, 60000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);

    if (typeof grecaptcha === "undefined") {
      console.error("reCAPTCHA client is not available.");
      setErrorMessage("reCAPTCHA client is not ready. Please try again later.");
      setLoading(false);
      return;
    }

    grecaptcha.ready(async () => {
      try {
        const token = await grecaptcha.execute(SITE_KEY, {
          action: "submit",
        });

        const response = await fetch("/api/send-email", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ...formData, token, formType: "appointment" }),
        });

        if (response.ok) {
          await response.json();
          setSuccessMessage(
            "Thank you! We've received your appointment request. Our team will contact you shortly."
          );
          setSubmitted(true);
          setErrorMessage("");

          // GTM event tracking
          if (typeof window !== "undefined" && window.dataLayer) {
            window.dataLayer.push({
              event: "trichos_form_submit",
              formType: "appointment",
              formId: "trichos-book-appointment-form",
              formName: "Book Appointment Form",
              submissionStatus: "success",
            });
          }

          // Redirect to thank you page after a short delay
          setTimeout(() => {
            window.location.href = "/thank-you-consultation";
          }, 1000);
        } else {
          const errorData = await response.json();
          setErrorMessage(errorData.message || "Error submitting form.");
          setSuccessMessage("");
        }
      } catch (error) {
        console.error("Error submitting form:", error);
        setErrorMessage("An error occurred. Please try again.");
        setSuccessMessage("");
      } finally {
        setLoading(false);
      }
    });
  };

  const handleReset = () => {
    setFormData({
      name: "",
      phone: "",
    });
    setSubmitted(false);
    setSuccessMessage("");
    setErrorMessage("");
  };

  return (
    <>
      <Script
        src={`https://www.google.com/recaptcha/api.js?render=${SITE_KEY}`}
        strategy="lazyOnload"
      />
      <div className="w-full overflow-hidden rounded-xl border border-gray-200 bg-gradient-to-br from-violet-50 to-purple-50">
        {submitted ? (
          <div className="p-6 text-center">
            {successMessage && (
              <p className="text-sm font-medium text-violet-900">
                {successMessage}
              </p>
            )}
            {errorMessage && (
              <p className="text-sm font-medium text-red-600">{errorMessage}</p>
            )}
            <button
              onClick={handleReset}
              className="mt-3 rounded-md bg-violet-950 px-4 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-violet-800"
            >
              Submit Another
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} id="trichos-book-appointment-form">

            {/* Body: Title */}
            <div className="px-4 pb-4 pt-2">
              <p className="m-1 text-center text-xl font-bold text-violet-950">
                Book a consultation
              </p>

              {/* Two fields side by side */}
              <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-violet-500 focus:outline-none focus:ring-1 focus:ring-violet-500"
                  placeholder="Name"
                />
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-violet-500 focus:outline-none focus:ring-1 focus:ring-violet-500"
                  placeholder="Mobile Number"
                />
              </div>

              {/* Call me Back Button */}
              <div className="flex justify-center">
                <button
                  type="submit"
                  disabled={loading}
                  className={`rounded-2xl border px-3 py-2 text-sm font-bold transition-all ${
                    loading
                      ? "cursor-not-allowed border-gray-400 bg-gray-400 text-gray-600"
                      : "border-black bg-black text-white shadow-md hover:bg-neutral-900"
                  }`}
                >
                  {loading ? (
                    <svg
                      className="mx-auto size-5 animate-spin"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                      ></path>
                    </svg>
                  ) : (
                    "Call me Back"
                  )}
                </button>
              </div>
            </div>

            {/* Footer */}
            <div className="relative border-t border-violet-200 bg-yellow-100 px-4 py-1 text-center">
              {isBusinessHours ? (
                <span className="m-2 flex items-center justify-center gap-1.5 text-sm font-bold leading-none text-green-600">
                  <FaPhone className="size-3.5 shrink-0" />
                  Our clinic team will call you back during working hours.
                </span>
              ) : (
                <span className="m-2 flex flex-col items-center justify-center gap-1 text-xs leading-tight">
                  <span className="font-semibold text-gray-700">
                    {afterHoursMessage}
                  </span>
                  {nextBusinessHour && (
                    <span className="text-gray-600">
                      Expected callback: {nextBusinessHour}
                    </span>
                  )}
                </span>
              )}
              {showTerms && (
                <span className="m-2 block text-[10px] leading-none text-gray-600">
                  By submitting, I agree to be contacted by Trichos. Protected
                  by{" "}
                  <Link
                    className="font-medium underline hover:text-violet-950"
                    href="https://policies.google.com/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Privacy Policy
                  </Link>{" "}
                  and{" "}
                  <Link
                    className="font-medium underline hover:text-violet-950"
                    href="https://policies.google.com/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Terms
                  </Link>
                  .
                </span>
              )}
              <button
                type="button"
                onClick={() => setShowTerms(!showTerms)}
                className="absolute right-2 top-1/2 flex -translate-y-1/2 items-center gap-1 text-[10px] text-gray-600 hover:text-gray-800"
                aria-label="Toggle terms"
              >
                <span>Terms</span>
                <FaChevronDown
                  className={`size-2.5 transition-transform ${
                    showTerms ? "rotate-180" : ""
                  }`}
                />
              </button>
            </div>
          </form>
        )}
      </div>
    </>
  );
}
